import { useAtomValue } from "jotai/index";
import first from "lodash/first";
import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";
import { FollowType } from "scmp-sdk";

import { accountAtom } from "shared/lib/account";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { homeHeroArticleContentItemContent$key } from "scmp-app/queries/__generated__/homeHeroArticleContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Headline,
  MoreOnThis,
  StyledContentHeadline,
  StyledEntityLink,
  StyledHomeFollowButton,
  Summary,
  TopicLink,
} from "./styles";

export type Props = Omit<ContentItemRenderVariantProps, "onClick" | "ref"> & {
  onClick?: (entityId?: string, urlAlias?: string, isMoreOnThis?: boolean) => void;
  reference: homeHeroArticleContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref: forwardReference,
  reference: reference_,
  topicLinkVariant,
}) => {
  const content = useFragment(
    graphql`
      fragment homeHeroArticleContentItemContent on Article {
        entityId
        urlAlias
        moreOnThisArticles {
          entityId
          urlAlias
          ... on Article {
            ...contentHeadlineContent
            ...entityLink
          }
        }
        topics {
          ...followButtonBase
        }
        ...contentTopicLinkContent
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withCoverImage: true
            withCoverImageSize1200x800: true
            withCoverImageSize540x360: true
            withCoverImageLeadingSlides: true
            withLiveTag: true
            withSummary: true
            withActionBar: true
            withTopicLink: true
            withVideoDurationOverlay: true
          )
      }
    `,
    reference_,
  );

  const providers = useContentItemProviders(content, topicLinkVariant);
  const moreOnThisArticle = first(content.moreOnThisArticles) ?? undefined;
  const topic = first(content.topics) ?? undefined;

  const user = useAtomValue(accountAtom);

  return (
    <Container className={className} ref={forwardReference}>
      <CoverImage>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias, false)}
          reference={content}
        >
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size1200x800",
              mobileUp: "size540x360",
              tabletUp: "size1200x800",
            },
            withCarousel: { imageType: "default" },
          })}
          {providers.video?.durationOverlay({
            iconSize: "large",
            variant: "homepage",
          })}
        </StyledEntityLink>
      </CoverImage>

      <Content>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias, false)}
          reference={content}
        >
          <LinkHighlightArea>
            <Headline>
              {providers.liveTag()}
              {providers.headline({
                hideTooltip: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            <Summary>
              {providers.summary({
                isTextOverflowEllipsis: true,
              })}
            </Summary>
          </LinkHighlightArea>
        </StyledEntityLink>

        <ActionBar>
          {providers.actionBar({
            commentVariant: "reverse",
            isBookmarkVisible: user.isLoggedIn,
            isCommentsVisible: true,
          })}
        </ActionBar>

        {moreOnThisArticle && (
          <MoreOnThis>
            <StyledEntityLink
              onClick={() =>
                onClick?.(moreOnThisArticle.entityId, moreOnThisArticle.urlAlias, true)
              }
              reference={moreOnThisArticle}
            >
              <StyledContentHeadline
                hideTag={false}
                preferSocialHeadline={true}
                reference={moreOnThisArticle}
                skipHighlight={true}
              />
            </StyledEntityLink>
          </MoreOnThis>
        )}

        <TopicLink>
          {providers.topicLink?.()}
          {topic && (
            <StyledHomeFollowButton
              isLoadingEnabled
              reference={topic}
              source="Top Story 1"
              type={FollowType.Topic}
            />
          )}
        </TopicLink>
      </Content>
    </Container>
  );
};

Component.displayName = "ContentItemHomeHeroArticle";

export const ContentItemHomeHeroArticle = withHighlightedHeadline(Component);
